import {PublicClientApplication} from '@azure/msal-browser';
import {msalConfig} from '../services/auth-provider';
import {
  DEFAULT_ERROR, TYPE_ERROR, apiV1Path
} from './constants';
import {store} from '../store/store';
import {encodeQueryData} from './helpers';
import RequestError from './RequestError';
import {setLockedUser} from '../store/currentUser/currentUser.slice';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';

dayjs.extend(utc);
dayjs.extend(timezone);

const userTimezone = dayjs.tz.guess();

const backendURI = process.env.REACT_APP_APPLICATION_BE_URI;

const contentTypes = {
  json: 'application/json',
  html: 'text/html',
  text: 'text/plain'
};

const stringifyBodyData = (data: object | undefined) => {
  if (!data) {
    return null;
  }
  try {
    return JSON.stringify(data);
  } catch (e) {
    throw new TypeError(TYPE_ERROR);
  }
};

const getResponseData = async (response) => {
  const contentType = response.headers.get('content-type');

  if (!contentType ||
    contentType.includes(contentTypes.text) ||
    contentType.includes(contentTypes.html)) {
    const responseText = await response.text();

    return {message: responseText};
  }

  if (response.status === 204) {
    return null;
  }

  if (contentType.includes(contentTypes.json)) {
    return response.json();
  }

  throw new TypeError(TYPE_ERROR);
};

const msalInstance = new PublicClientApplication(msalConfig);

const acquireAccessToken = async (msalInstance: PublicClientApplication) => {
  const activeAccount = msalInstance.getActiveAccount();
  const accounts = msalInstance.getAllAccounts();

  if (!activeAccount && accounts.length === 0) {
    return null;
  }
  const request = {
    scopes: [`${process.env.REACT_APP_APPLICATION_SCOPES}`, 'openid', 'offline_access'],
    account: activeAccount || accounts[0]
  };

  const authResult = await msalInstance.acquireTokenSilent(request);

  return authResult.accessToken;
};

const sendRequest = async (url = '', options = {}) => {
  let response;
  const fetchUrl = `${backendURI}${apiV1Path}${url}`;
  const token = await acquireAccessToken(msalInstance);

  const requestHeaders = new Headers({
    'Content-Type': contentTypes.json
  });

  if (token) {
    requestHeaders.append('Authorization', `Bearer ${token}`);
  }

  const {permissions} = store.getState() || {};
  const {activeTenant} = permissions || {};

  requestHeaders.append('tenant-id', activeTenant);
  requestHeaders.append('client-time-zone', userTimezone);

  const requestOptions = {
    headers: requestHeaders,
    ...options
  };

  try {
    response = await fetch(fetchUrl, requestOptions);
  } catch (e) {
    throw new TypeError(DEFAULT_ERROR);
  }

  const responseData = await getResponseData(response);

  if (!response.ok) {
    if (responseData.statusCode === 401) {
      msalInstance.logoutRedirect();
    } else if (responseData.errorCode === 'LOCKED_USER' &&
    responseData.statusCode === 403) {
      store.dispatch(setLockedUser(true));
    } else {
      throw new RequestError(response.status, responseData);
    }
  }

  return responseData;
};

export const sendGetRequest = (url: string, data?: object) => {
  const requestUrl = url + encodeQueryData(data);

  const requestOptions = {
    method: 'GET'
  };

  return sendRequest(requestUrl, requestOptions);
};

export const sendPostRequest = (
    url: string, data?: object | null, params?: object
) => {
  const requestUrl = params ? url + encodeQueryData(params) : url;

  const requestOptions = data ? {
    method: 'POST',
    body: stringifyBodyData(data)
  } : {
    method: 'POST'
  };

  return sendRequest(requestUrl, requestOptions);
};

export const sendPutRequest = (url: string, data?: object) => {
  const requestOptions = {
    method: 'PUT',
    body: stringifyBodyData(data)
  };

  return sendRequest(url, requestOptions);
};

export const sendDeleteRequest = (url: string, data?: object) => {
  const requestOptions = {
    method: 'DELETE',
    body: stringifyBodyData(data)
  };

  return sendRequest(url, requestOptions);
};
